import { useMemo } from "react";
import { useCalculateOdds } from "./useCalculateOdds";
import { useCalculateWinningTax } from "./useCalculateWinningTax";
import { useCalculateVat } from "./useCalculateVat";
import { useCalculateWinBoost } from "./useCalculateWinBoost";
import { useCalculateMaxPayout } from "./useCalculateMaxPayout";
import { BetSlipMode, OddStatus } from "../types/enums";
import { getBonusAmount, resolveFreeBetConditions, selectActiveBonuses } from "../shared/utils/bonus-utils";

interface UseSummaryCalculationsInput {
  betSlipState: IBetSlipState;
  config: IWidgetConfig;
  individualStakes: { [key: string]: number };
  globalStake: number;
  selectedBonus: IBetSlipBonus | null;
  rawBonuses: IBetSlipBonus[];
}

export const useSummaryCalculations = ({
  betSlipState,
  config,
  individualStakes,
  globalStake,
  selectedBonus,
  rawBonuses,
}: UseSummaryCalculationsInput) => {
  const isFreeBetActive = !!selectedBonus;
  const freeBetStake = selectedBonus ? getBonusAmount(selectedBonus) : 0;

  const currencySportsBook = useMemo(
    () => config.brand.currencyConfigurations?.[config.user.currencyId]?.sportsBook,
    [config.brand.currencyConfigurations, config.user.currencyId],
  );

  const { winBoosts } = useMemo(
    () => selectActiveBonuses(rawBonuses, config.brand.staticBonuses, config.user.currencyId),
    [rawBonuses, config.brand.staticBonuses, config.user.currencyId],
  );

  const totalOdds = useCalculateOdds({
    selections: betSlipState.selections,
    betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
    mode: betSlipState.mode,
  });

  const totalSelections = useMemo(() => {
    const regular = betSlipState.selections.length;
    const betBuilder = betSlipState.betBuilderState?.totalOdds?.length || 0;
    return regular + betBuilder;
  }, [betSlipState.selections, betSlipState.betBuilderState]);

  const singleSelectionOddPrice =
    totalSelections === 1 ? betSlipState.selections[0]?.oddPrice : undefined;

  const isFreeBetEligible = useMemo(
    () =>
      !selectedBonus ||
      resolveFreeBetConditions(
        selectedBonus,
        betSlipState.mode,
        totalSelections,
        totalOdds,
        singleSelectionOddPrice,
      ).isEligible,
    [selectedBonus, betSlipState.mode, totalSelections, totalOdds, singleSelectionOddPrice],
  );

  const totalStake = useMemo(() => {
    if (isFreeBetActive) {
      // Legacy applies the free bet amount as the stake of every single-mode
      // selection (bet-slip.model.ts accumulator()'s getStake()), so total
      // exposure is the free bet amount per selection, not a single flat value.
      return betSlipState.mode === BetSlipMode.Single
        ? freeBetStake * betSlipState.selections.length
        : freeBetStake;
    }
    if (betSlipState.mode === BetSlipMode.Single) {
      return (Object as any)
        .values(individualStakes)
        .reduce((sum: number, stake: number) => sum + stake, 0);
    }
    return globalStake;
  }, [
    isFreeBetActive,
    freeBetStake,
    betSlipState.mode,
    betSlipState.selections,
    individualStakes,
    globalStake,
  ]);

  const stakes = useMemo(() => {
    if (isFreeBetActive && betSlipState.mode === BetSlipMode.Single) {
      return Object.fromEntries(betSlipState.selections.map((s) => [s.key, freeBetStake]));
    }
    if (betSlipState.mode === BetSlipMode.Single) return individualStakes;
    return { combo: globalStake };
  }, [
    isFreeBetActive,
    freeBetStake,
    betSlipState.mode,
    betSlipState.selections,
    individualStakes,
    globalStake,
  ]);

  const taxConfig = useMemo(
    () => ({
      maxPayout: currencySportsBook?.maxPayout ?? config.brand.maxPayout,
      taxes: config.brand.taxes,
      minFactorDown: currencySportsBook?.minFactorDown ?? config.brand.minFactorDown,
      maxBet: currencySportsBook?.maxBet ?? config.user.maxBet,
      taxRefundTiers: [],
    }),
    [
      currencySportsBook,
      config.brand.maxPayout,
      config.brand.taxes,
      config.brand.minFactorDown,
      config.user.maxBet,
    ],
  );

  const { exciseTaxValue, exciseTaxRate, netStake } = useCalculateVat({
    selections: betSlipState.selections,
    config: taxConfig,
    stakes,
    mode: betSlipState.mode,
  });

  const showVat = exciseTaxRate > 0 && !isFreeBetActive;

  const netStakes = useMemo(() => {
    if (!showVat) return stakes;
    const divisor = 1 + exciseTaxRate / 100;
    if (betSlipState.mode === BetSlipMode.Single) {
      const scaled: { [key: string]: number } = {};
      Object.entries(individualStakes).forEach(([key, gross]) => {
        scaled[key] = gross / divisor;
      });
      return scaled;
    }
    return { combo: globalStake / divisor };
  }, [showVat, exciseTaxRate, betSlipState.mode, individualStakes, globalStake, stakes]);

  const { activeWinBoost, isWinBoostEligible, winBoostAmount } =
    useCalculateWinBoost({
      winBoosts,
      totalSelections,
      selections: betSlipState.selections,
      betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
      stakes: netStakes,
      totalOdds,
      totalStake,
      mode: betSlipState.mode,
      isFreeBetActive,
    });

  const { totalTaxValue, taxPercentage } = useCalculateWinningTax({
    selections: betSlipState.selections,
    config: taxConfig,
    bonus: selectedBonus || undefined,
    stakes: netStakes,
    mode: betSlipState.mode,
    bonusValue: winBoostAmount,
    betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
  });

  const maxPayout = useCalculateMaxPayout({
    selections: betSlipState.selections,
    stakes: netStakes,
    mode: betSlipState.mode,
    totalOdds,
    totalTaxValue,
    winBoostAmount,
    isFreeBetActive,
  });

  const hasSuspendedOrClosedBBBets = useMemo(
    () =>
      betSlipState.betBuilderState?.bets?.some(
        (bet) => bet.suspended || bet.closed,
      ) ?? false,
    [betSlipState.betBuilderState?.bets],
  );

  const hasOddsChanges = useMemo(() => {
    if (betSlipState.acceptOddsChanges) return false;
    const hasSelectionChanges = betSlipState.selections.some((sel) => {
      const hasOddChange =
        sel.oldOddPrice && sel.oldOddPrice !== sel.oddPrice;
      const hasStatusChange =
        sel.betStatus === OddStatus.Suspended ||
        sel.betStatus === OddStatus.Closed;
      return hasOddChange || hasStatusChange;
    });
    const hasBBOddsWorsened =
      betSlipState.betBuilderState?.bets?.some(
        (bet) => bet.previouseOdds != null && bet.odds < bet.previouseOdds,
      ) ?? false;
    const hasBBTotalOddsWorsened =
      betSlipState.betBuilderState?.totalOdds?.some(
        (to) => to.previouseOdds > 0 && to.odd < to.previouseOdds,
      ) ?? false;
    return (
      hasSelectionChanges ||
      hasSuspendedOrClosedBBBets ||
      hasBBOddsWorsened ||
      hasBBTotalOddsWorsened
    );
  }, [
    betSlipState.acceptOddsChanges,
    betSlipState.selections,
    betSlipState.betBuilderState?.bets,
    betSlipState.betBuilderState?.totalOdds,
    hasSuspendedOrClosedBBBets,
  ]);

  const hasClosedOrSuspendedSelections = useMemo(
    () =>
      betSlipState.selections.some(
        (sel) =>
          sel.betStatus === OddStatus.Suspended ||
          sel.betStatus === OddStatus.Closed,
      ) || hasSuspendedOrClosedBBBets,
    [betSlipState.selections, hasSuspendedOrClosedBBBets],
  );

  return {
    isFreeBetActive,
    isFreeBetEligible,
    freeBetStake,
    totalOdds,
    totalSelections,
    totalStake,
    stakes,
    taxConfig,
    exciseTaxValue,
    exciseTaxRate,
    netStake,
    showVat,
    netStakes,
    activeWinBoost,
    isWinBoostEligible,
    winBoostAmount,
    totalTaxValue,
    taxPercentage,
    maxPayout,
    hasOddsChanges,
    hasClosedOrSuspendedSelections,
  };
};
